import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// 计算器核心逻辑
export const useCalculatorStore = defineStore('calculator', () => {
  // 状态管理
  const waterVolumeState = ref<number | null>(null)

  // 常量定义
  const saltBrands = ref({
    'red-sea-coral-pro': { name: '红海珊瑚专业盐', density: 36.5 },
    'tropic-marin-pro-reef': { name: 'TM Pro-Reef', density: 36.0 },
    'fauna-marin-pro-master': { name: 'FM Pro Master', density: 36.2 },
    'instant-ocean-reef-crystals': { name: 'Instant Ocean Reef Crystals', density: 35.5 },
    'brightwell-neomarine': { name: 'Brightwell NeoMarine', density: 36.0 },
    'custom': { name: '自定义', density: 36.0 }
  })

  const additiveBrands = ref({
    'alk': {
      'brs-2part': { name: 'BRS 两液法碱度', concentration: 3.8 },
      'red-sea-foundation-a': { name: '红海基础液A', concentration: 2.5 },
      'diy-nahco3': { name: 'DIY碳酸氢钠(100g/L)', concentration: 2.8 },
      'pure-na2co3': { name: '分析纯无水碳酸钠', concentration: 5.6, gramsPerUnit: '5.6 dKH' },
      'custom': { name: '自定义', concentration: 2.8 }
    },
    'ca': {
      'brs-2part': { name: 'BRS 两液法钙', concentration: 35 },
      'red-sea-foundation-b': { name: '红海基础液B', concentration: 28 },
      'diy-cacl2': { name: 'DIY氯化钙(100g/L)', concentration: 36 },
      'pure-cacl2': { name: '分析纯无水CaCl₂', concentration: 72, gramsPerUnit: '72 ppm' },
      'custom': { name: '自定义', concentration: 35 }
    },
    'mg': {
      'brs-2part': { name: 'BRS 两液法镁', concentration: 9.8 },
      'diy-mgso4': { name: 'DIY硫酸镁(100g/L)', concentration: 9.8 },
      'pure-mgso4': { name: '分析纯七水硫酸镁', concentration: 9.8, gramsPerUnit: '9.8 ppm' },
      'custom': { name: '自定义', concentration: 9.8 }
    },
    'k': {
      'pure-kcl': { name: '分析纯氯化钾', concentration: 52.4, gramsPerUnit: '52.4 ppm' },
      'custom': { name: '自定义', concentration: 52.4 }
    }
  })

  // 辅助函数
  const round = (value: number, decimals: number): number => {
    return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals)
  }

  // 1. 水体基础计算
  const calculateWaterVolume = (
    length: number, 
    width: number, 
    height: number, 
    unit: string = 'cm', 
    rockPercentage: number = 15, 
    sandPercentage: number = 5
  ) => {
    let volumeCm3: number
    
    if (unit === 'cm') {
      volumeCm3 = length * width * height
    } else if (unit === 'inch') {
      volumeCm3 = length * width * height * 16.387 // 立方英寸转立方厘米
    } else {
      volumeCm3 = 0
    }
    
    // 理论水量（升）
    const theoreticalVolume = volumeCm3 / 1000
    
    // 扣除活石和底砂体积
    const rockVolume = theoreticalVolume * (rockPercentage / 100)
    const sandVolume = theoreticalVolume * (sandPercentage / 100)
    const actualVolume = theoreticalVolume - rockVolume - sandVolume
    
    return {
      theoreticalVolume: round(theoreticalVolume, 1),
      actualVolume: round(actualVolume, 1),
      rockVolume: round(rockVolume, 1),
      sandVolume: round(sandVolume, 1)
    }
  }

  // 2. 换水计算器
  const calculateWaterChange = (
    totalVolume: number, 
    changePercentage: number | null = null, 
    changeVolume: number | null = null, 
    saltBrand: string = 'tropic-marin-pro-reef'
  ) => {
    let waterToChange: number, newSaltwater: number
    
    if (changePercentage !== null) {
      waterToChange = totalVolume * (changePercentage / 100)
      newSaltwater = waterToChange
    } else if (changeVolume !== null) {
      waterToChange = changeVolume
      newSaltwater = changeVolume
    } else {
      return null
    }

    const saltDensity = saltBrands.value[saltBrand as keyof typeof saltBrands.value].density
    const saltNeeded = newSaltwater * saltDensity

    // 换水频率建议
    let frequencyAdvice = ''
    if (changePercentage !== null) {
      if (changePercentage <= 10) {
        frequencyAdvice = '建议每周换水1-2次'
      } else if (changePercentage <= 20) {
        frequencyAdvice = '建议每周换水1次'
      } else {
        frequencyAdvice = '建议每2周换水1次'
      }
    }

    return {
      waterToChange: round(waterToChange, 1),
      newSaltwater: round(newSaltwater, 1),
      saltNeeded: round(saltNeeded, 1),
      frequencyAdvice
    }
  }

  // 3. 盐度比重换算
  const salinityToSG = (salinity: number, temperature: number = 25): number => {
    let sg = 1 + (salinity / 1000) * 0.766
    
    // 温度补偿
    if (temperature !== 25) {
      const tempCorrection = (temperature - 25) * 0.0002
      sg += tempCorrection
    }
    
    return round(sg, 4)
  }

  const sgToSalinity = (sg: number, temperature: number = 25): number => {
    // 温度补偿
    let correctedSG = sg
    if (temperature !== 25) {
      const tempCorrection = (temperature - 25) * 0.0002
      correctedSG -= tempCorrection
    }
    
    const salinity = ((correctedSG - 1) / 0.766) * 1000
    return round(salinity, 1)
  }

  // 4. 元素补充计算
  const calculateElementDosage = (
    element: string,
    currentValue: number,
    targetValue: number,
    volume: number,
    brand: string,
    unit: string = 'dKH',
    customConcentration: number | null = null
  ) => {
    let conversionFactor = 1
    let additiveConcentration: number
    
    // 使用自定义浓度或预设浓度
    if (brand === 'custom' && customConcentration) {
      additiveConcentration = customConcentration
    } else {
      // 修复类型问题
      const elementBrands = additiveBrands.value[element as keyof typeof additiveBrands.value]
      if (elementBrands && brand in elementBrands) {
        const selectedBrand = elementBrands[brand as keyof typeof elementBrands]
        additiveConcentration = selectedBrand.concentration
      } else {
        additiveConcentration = 1 // 默认值
      }
    }
    
    // 单位转换
    if (element === 'alk') {
      if (unit === 'meq/L') {
        conversionFactor = 1
      } else if (unit === 'dKH') {
        conversionFactor = 1 // dKH不需要转换，浓度已经是dKH/mL
      }
    }
    
    const difference = targetValue - currentValue
    const dosageML = (difference * volume) / additiveConcentration
    
    // 安全检查
    let safetyNote = ''
    let isSafe = true
    
    if (element === 'alk') {
      const dailyChange = Math.abs(difference) / conversionFactor
      if (dailyChange > 1.0) {
        safetyNote = '警告：单次碱度提升勿超 1 dKH，建议分次添加'
        isSafe = false
      }
    } else if (element === 'ca') {
      const dailyChange = Math.abs(targetValue - currentValue)
      if (dailyChange > 20) {
        safetyNote = '警告：单次钙提升勿超 20 ppm，建议分次添加'
        isSafe = false
      }
    } else if (element === 'k') {
      const dailyChange = Math.abs(targetValue - currentValue)
      if (dailyChange > 10) {
        safetyNote = '警告：单次钾提升勿超 10 ppm，建议分次添加'
        isSafe = false
      }
    }

    // 获取附加信息
    let additionalInfo = ''
    const elementBrands = additiveBrands.value[element as keyof typeof additiveBrands.value]
    if (elementBrands && brand in elementBrands) {
      const selectedBrand = elementBrands[brand as keyof typeof elementBrands]
      if ('gramsPerUnit' in selectedBrand && selectedBrand.gramsPerUnit) {
        additionalInfo = `使用${selectedBrand.name}，每克可提升${selectedBrand.gramsPerUnit}`
      }
    }
    
    return {
      dosageML: round(Math.max(0, dosageML), 1),
      safetyNote,
      isSafe,
      dailyChange: round(Math.abs(targetValue - currentValue), 1),
      concentrationUsed: additiveConcentration,
      additionalInfo
    }
  }

  // 5. 营养盐稀释计算
  const calculateNutrientDilution = (
    currentConcentration: number, 
    targetConcentration: number, 
    totalVolume: number
  ) => {
    if (currentConcentration <= targetConcentration) {
      return {
        waterToChange: 0,
        percentage: 0,
        note: '当前浓度已低于或等于目标浓度，无需换水'
      }
    }
    
    const waterToChange = totalVolume * (1 - (targetConcentration / currentConcentration))
    const percentage = (waterToChange / totalVolume) * 100
    
    return {
      waterToChange: round(waterToChange, 1),
      percentage: round(percentage, 1),
      note: `需要换水 ${round(percentage, 1)}% 来降低浓度`
    }
  }

  // 水体体积管理
  const setWaterVolume = (volume: number) => {
    waterVolumeState.value = volume
    localStorage.setItem('waterVolume', volume.toString())
  }

  const getWaterVolume = () => {
    if (waterVolumeState.value === null) {
      const stored = localStorage.getItem('waterVolume')
      waterVolumeState.value = stored ? parseFloat(stored) : null
    }
    return waterVolumeState.value
  }

  const clearWaterVolume = () => {
    waterVolumeState.value = null
    localStorage.removeItem('waterVolume')
  }

  return {
    // 状态
    waterVolume: computed(() => waterVolumeState.value),
    
    // 常量
    saltBrands,
    additiveBrands,
    
    // 计算方法
    calculateWaterVolume,
    calculateWaterChange,
    salinityToSG,
    sgToSalinity,
    calculateElementDosage,
    calculateNutrientDilution,
    round,
    
    // 水体体积管理
    setWaterVolume,
    getWaterVolume,
    clearWaterVolume
  }
})